import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MapPin, 
  Hotel, 
  Car, 
  Compass, 
  MessageSquare, 
  HelpCircle,
  Settings,
  CalendarCheck,
  LogOut,
  Menu,
  X,
  ChevronRight,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84027868-605e-4a17-95ea-ac9185a5f86d/artifacts/v7atgd7h_Impulsodigital2030%40gmail.com_20260410_215902_0000.png";

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: CalendarCheck, label: 'Reservaciones', path: '/admin/reservations' },
  { icon: MapPin, label: 'Zonas', path: '/admin/zones' },
  { icon: Hotel, label: 'Hoteles', path: '/admin/hotels' },
  { icon: DollarSign, label: 'Precios Rutas', path: '/admin/route-prices' },
  { icon: Car, label: 'Vehículos', path: '/admin/vehicles' },
  { icon: Compass, label: 'Excursiones', path: '/admin/excursions' },
  { icon: MessageSquare, label: 'Testimonios', path: '/admin/testimonials' },
  { icon: HelpCircle, label: 'FAQs', path: '/admin/faqs' },
  { icon: Settings, label: 'Ajustes', path: '/admin/settings' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0F172A] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Ferr Tours" className="h-10" />
              <span className="text-white font-bold">Admin</span>
            </Link>
          </div>

          {/* Menu */}
          <nav className="flex-1 py-6 px-4 overflow-y-auto">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-400 hover:bg-white/5 hover:text-white'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`admin-nav-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <span className="text-cyan-400 font-bold">{user?.name?.[0] || 'A'}</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">{user?.name || 'Admin'}</p>
                <p className="text-slate-500 text-xs">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-400/10"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#0F172A]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="admin-mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <div className="hidden lg:block">
              <h1 className="text-xl font-bold text-white">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Admin'}
              </h1>
            </div>

            <Link 
              to="/" 
              target="_blank"
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              View Site →
            </Link>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
